import React, { useRef } from 'react';
import { FaBold, FaItalic, FaLink, FaListUl, FaHeading, FaParagraph, FaLevelUpAlt } from 'react-icons/fa';
import './HtmlEditor.css';

/**
 * Editor de texto con barra de herramientas para insertar HTML.
 * Los botones evitan quitar el foco del textarea (onMouseDown preventDefault) para que la selección no se pierda.
 */
const HtmlEditor = ({ value = '', onChange, placeholder, rows = 8, label, id }) => {
  const textareaRef = useRef(null);

  const insertAtCursor = (before, after = '', cursorOffset = 0) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = value || '';
    const selected = text.slice(start, end);
    const newText = text.slice(0, start) + before + selected + after + text.slice(end);
    onChange(newText);
    const newPos = start + before.length + selected.length + cursorOffset;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newPos, newPos);
    });
  };

  const wrapSelection = (tag, placeholder = '') => {
    const ta = textareaRef.current;
    const hasSelection = ta && ta.selectionStart !== ta.selectionEnd;
    if (hasSelection) {
      insertAtCursor(`<${tag}>`, `</${tag}>`);
    } else if (placeholder) {
      insertAtCursor(`<${tag}>${placeholder}`, `</${tag}>`, -placeholder.length);
    } else {
      insertAtCursor(`<${tag}>`, `</${tag}>`, -`</${tag}>`.length);
    }
  };

  const insertHeading = (level) => {
    wrapSelection(`h${level}`, `Título ${level}`);
  };

  const insertLink = () => {
    const url = prompt('URL del enlace:', 'https://');
    if (url) insertAtCursor(`<a href="${url}" target="_blank" rel="noopener noreferrer">`, '</a>', -4);
  };

  const insertList = () => {
    insertAtCursor('<ul>\n  <li>', '</li>\n</ul>\n', 0);
  };

  return (
    <div className="html-editor-wrap">
      {label && <label htmlFor={id}>{label}</label>}
      <div className="html-editor-toolbar">
        <button type="button" onMouseDown={(e) => { e.preventDefault(); wrapSelection('strong', 'negrita'); }} title="Negrita (Ctrl+B)">
          <FaBold />
        </button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); wrapSelection('em', 'cursiva'); }} title="Cursiva (Ctrl+I)">
          <FaItalic />
        </button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); insertHeading(2); }} title="Título 2">
          <FaHeading />
        </button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); wrapSelection('p', 'párrafo'); }} title="Párrafo">
          <FaParagraph />
        </button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); insertList(); }} title="Lista">
          <FaListUl />
        </button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); insertLink(); }} title="Enlace">
          <FaLink />
        </button>
        <button type="button" onMouseDown={(e) => { e.preventDefault(); insertAtCursor('<br />'); }} title="Salto de línea">
          <FaLevelUpAlt />
        </button>
      </div>
      <textarea
        ref={textareaRef}
        id={id}
        className="html-editor-textarea"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        spellCheck="true"
      />
      <small className="html-editor-hint">Puedes usar HTML: &lt;strong&gt;, &lt;em&gt;, &lt;p&gt;, &lt;ul&gt;&lt;li&gt;, &lt;a href="..."&gt;</small>
    </div>
  );
};

export default HtmlEditor;
